"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CONTACT_EMAIL } from "@/lib/constants";
import {
  contactSchema,
  departments,
  services,
  type ContactFormData,
} from "@/lib/contactSchema";
import { trackEvent } from "@/lib/analytics";
import { useTranslation } from "@/lib/i18n";

export function ContactForm() {
  const { t, language } = useTranslation();
  const privacyHref = language === "bg" ? "/bg/legal/privacy" : "/legal/privacy";

  const [formData, setFormData] = useState<Partial<ContactFormData>>({
    name: "",
    company: "",
    email: "",
    phone: "",
    department: undefined,
    service: undefined,
    message: "",
  });

  /* Pre-fill from URL params (sent by UnifiedConfigurator). Read from
     window.location on mount rather than useSearchParams(), which would make
     the form client-only on the statically rendered /contact/ page. */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const dept = searchParams.get("department");
    const svc = searchParams.get("service");
    const msg = searchParams.get("message");
    if (dept || svc || msg) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: syncing effect-driven state
      setFormData((prev) => ({
        ...prev,
        ...(dept ? { department: dept as ContactFormData["department"] } : {}),
        ...(svc ? { service: svc as ContactFormData["service"] } : {}),
        ...(msg ? { message: msg } : {}),
      }));
    }
  }, []);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field on change
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const result = contactSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(result.data),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("success");
      trackEvent("contact_form_submit", {
        department: result.data.department ?? "unspecified",
        service: result.data.service ?? "unspecified",
      });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
    }
  }

  if (status === "error") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bento-card text-center py-16"
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('contact_form_error_title')}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t('contact_form_error_text_prefix')}{" "}
          <a href={`mailto:${CONTACT_EMAIL.sales}`} className="text-accent-text hover:underline">{CONTACT_EMAIL.sales}</a>{" "}
          {t('contact_form_error_text_or')} <a href="tel:+35924200300" className="text-accent-text hover:underline">+359 2 420 0300</a>.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 px-5 py-2 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
        >
          {t('contact_form_try_again')}
        </button>
      </motion.div>
    );
  }

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bento-card text-center py-16"
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('contact_form_success_title')}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t('contact_form_success_text')}
        </p>
        <button
          onClick={() => {
            setStatus("idle");
            setFormData({
              name: "", company: "", email: "", phone: "",
              department: undefined, service: undefined, message: "",
            });
          }}
          className="mt-6 px-5 py-2 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
        >
          {t('contact_form_send_another')}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bento-card" noValidate aria-label={t('contact_form_title')}>
      <div className="relative z-10 space-y-6">
        <div className="mb-2">
          <h2 className="text-xl font-bold text-foreground">{t('contact_form_title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('contact_form_subtitle')}
          </p>
        </div>

        {/* Name + Company row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={t('contact_form_name')}
            name="name"
            type="text"
            placeholder={t('contact_form_name_placeholder')}
            value={formData.name || ""}
            onChange={handleChange}
            error={errors.name}
            required
          />
          <Field
            label={t('contact_form_company')}
            name="company"
            type="text"
            placeholder={t('contact_form_company_placeholder')}
            value={formData.company || ""}
            onChange={handleChange}
            error={errors.company}
            required
          />
        </div>

        {/* Email + Phone row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={t('contact_form_email')}
            name="email"
            type="email"
            placeholder={t('contact_form_email_placeholder')}
            value={formData.email || ""}
            onChange={handleChange}
            error={errors.email}
            required
          />
          <Field
            label={t('contact_form_phone')}
            name="phone"
            type="tel"
            placeholder={t('contact_form_phone_placeholder')}
            value={formData.phone || ""}
            onChange={handleChange}
            error={errors.phone}
          />
        </div>

        {/* Department + Service row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField
            label={t('contact_form_department')}
            name="department"
            value={formData.department || ""}
            onChange={handleChange}
            error={errors.department}
            options={departments}
            placeholder={t('contact_form_department_placeholder')}
            required
          />
          <SelectField
            label={t('contact_form_service')}
            name="service"
            value={formData.service || ""}
            onChange={handleChange}
            error={errors.service}
            options={services}
            placeholder={t('contact_form_service_placeholder')}
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="field-message" className="block text-sm font-medium text-foreground mb-1.5">
            {t('contact_form_message')} <span className="text-accent-text">*</span>
          </label>
          <textarea
            id="field-message"
            name="message"
            rows={5}
            value={formData.message || ""}
            onChange={handleChange}
            placeholder={t('contact_form_message_placeholder')}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "field-message-error" : undefined}
            className={`w-full px-4 py-3 rounded-xl bg-surface border ${
              errors.message ? "border-red-500" : "border-border"
            } text-foreground placeholder:text-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none`}
          />
          {errors.message && (
            <p id="field-message-error" className="mt-1 text-xs text-red-400" role="alert">{errors.message}</p>
          )}
        </div>

        {/* GDPR Art. 13 notice — rendered above the submit button in the
            same pattern already used by InsightsSubscribeForm and
            PeeringRequestForm on this site. */}
        <p className="text-xs text-muted-foreground leading-relaxed">
          {t("form_notice_contact")}{" "}
          <a href={privacyHref} className="underline hover:text-text-primary">
            {t("form_notice_privacy_link")}
          </a>
          .
        </p>

        {/* Submit */}
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium bg-primary text-on-primary rounded-xl hover:bg-primary-light transition-all duration-300 glow-blue disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
              </svg>
              {t('contact_form_submitting')}
            </>
          ) : (
            <>
              {t('contact_form_submit')}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

/* ── Reusable field components ─────────────────────────────────── */

function Field({
  label, name, type, placeholder, value, onChange, error, required,
}: {
  label: string; name: string; type: string; placeholder: string;
  value: string; onChange: React.ChangeEventHandler<HTMLInputElement>;
  error?: string; required?: boolean;
}) {
  const inputId = `field-${name}`;
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-1.5">
        {label} {required && <span className="text-accent-text">*</span>}
      </label>
      <input
        id={inputId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={`w-full px-4 py-2.5 rounded-xl bg-surface border ${
          error ? "border-red-500" : "border-border"
        } text-foreground placeholder:text-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors`}
      />
      {error && <p id={`${inputId}-error`} className="mt-1 text-xs text-red-400" role="alert">{error}</p>}
    </div>
  );
}

function SelectField({
  label, name, value, onChange, error, options, placeholder, required,
}: {
  label: string; name: string; value: string;
  onChange: React.ChangeEventHandler<HTMLSelectElement>;
  error?: string; options: { value: string; label: string }[];
  placeholder: string; required?: boolean;
}) {
  const selectId = `field-${name}`;
  return (
    <div>
      <label htmlFor={selectId} className="block text-sm font-medium text-foreground mb-1.5">
        {label} {required && <span className="text-accent-text">*</span>}
      </label>
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${selectId}-error` : undefined}
        className={`w-full px-4 py-2.5 rounded-xl bg-surface border ${
          error ? "border-red-500" : "border-border"
        } text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors appearance-none cursor-pointer`}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' fill='none' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
        }}
      >
        <option value="" disabled className="text-muted bg-surface">
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-surface text-foreground">
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p id={`${selectId}-error`} className="mt-1 text-xs text-red-400" role="alert">{error}</p>}
    </div>
  );
}
